import { useState } from 'react';
import {
  useApproveTutorApplication,
  useRejectTutorApplication,
  useRequestTutorChanges,
  useRevealTutorPayoutAccount,
  useSuspendTutor,
  useTutorApplication,
  useViewTutorDocument,
} from '../../api/tutor-applications';
import { ApplicationStatusBadge } from '../../components/Badge';
import { Modal } from '../../components/Modal';
import { Spinner } from '../../components/Spinner';
import { ErrorState } from '../../components/EmptyState';
import { ApiError, uploadUrl } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import type { TutorDocument } from '../../types/api';

/** Which fields a "request changes" can name, in the words the app shows. */
const CHANGEABLE: { value: string; label: string }[] = [
  { value: 'avatar', label: 'Profile photo' },
  { value: 'document', label: 'ID document' },
  { value: 'bio', label: 'Bio' },
  { value: 'subjects', label: 'Subjects' },
  { value: 'levels', label: 'Stages' },
  { value: 'introVideoUrl', label: 'Intro video' },
  { value: 'payoutAccount', label: 'Payout details' },
];

const SIDE_LABEL: Record<TutorDocument['side'], string> = {
  front: 'Front',
  back: 'Back',
  single: 'Photo page',
};

const DOC_TYPE_LABEL: Record<TutorDocument['docType'], string> = {
  national_id: 'National ID',
  residence_permit: 'Residence permit',
  passport: 'Passport',
};

/**
 * The review screen, as a drawer over the queue.
 *
 * It lays out side by side the things §5.3 asks a reviewer to compare: the
 * ID document, the photo, the name as typed, the payout holder name, the
 * bio, the subjects and stages, and the video. The one thing it does not do
 * is decide — the API refuses a move the state machine does not allow, so
 * every button here is an ask rather than an assertion.
 */
export function ReviewDrawer({
  tutorId,
  defaultCommissionPercent,
  onClose,
}: {
  tutorId: string;
  defaultCommissionPercent: number;
  onClose: () => void;
}) {
  const application = useTutorApplication(tutorId);
  const viewDocument = useViewTutorDocument();
  const reveal = useRevealTutorPayoutAccount();

  const [dialog, setDialog] = useState<'approve' | 'changes' | 'reject' | 'suspend' | null>(
    null,
  );
  const [note, setNote] = useState('');
  const [fields, setFields] = useState<string[]>([]);
  const [commission, setCommission] = useState(String(defaultCommissionPercent));
  const [actionError, setActionError] = useState<string | null>(null);

  const approve = useApproveTutorApplication();
  const requestChanges = useRequestTutorChanges();
  const reject = useRejectTutorApplication();
  const suspend = useSuspendTutor();
  const pending =
    approve.isPending || requestChanges.isPending || reject.isPending || suspend.isPending;

  const data = application.data;
  const awaitingReview = data?.applicationStatus === 'submitted';

  const closeDialog = () => {
    setDialog(null);
    setNote('');
    setFields([]);
    setActionError(null);
  };

  async function run(action: () => Promise<unknown>) {
    setActionError(null);
    try {
      await action();
      closeDialog();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'That did not work.');
    }
  }

  return (
    <aside
      className="fixed inset-y-0 end-0 z-40 flex w-full max-w-xl flex-col border-s border-rule bg-surface shadow-xl"
      aria-label="Tutor application"
    >
      <header className="flex items-center justify-between gap-3 border-b border-rule px-5 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-ink">
            {data?.fullName ?? 'Application'}
          </h2>
          {data && <p className="truncate text-xs text-ink-3">{data.email}</p>}
        </div>
        <div className="flex items-center gap-2">
          {data && <ApplicationStatusBadge status={data.applicationStatus} />}
          <button type="button" className="btn btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {application.isLoading && <Spinner />}
        {application.isError && (
          <ErrorState
            message={
              application.error instanceof ApiError ? application.error.message : undefined
            }
            onRetry={() => void application.refetch()}
          />
        )}

        {data && (
          <div className="space-y-5">
            {/* The photo and the ID, together: the reviewer's first job is
                deciding they are the same person. */}
            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">Identity</h3>
              <div className="flex gap-4">
                <span className="size-24 shrink-0 overflow-hidden rounded-lg border border-rule bg-surface-2">
                  {data.avatarUrl ? (
                    <img
                      src={uploadUrl(data.avatarUrl)}
                      alt={data.fullName}
                      className="size-full object-cover"
                    />
                  ) : (
                    <span className="flex size-full items-center justify-center text-xs text-ink-3">
                      No photo
                    </span>
                  )}
                </span>
                <div className="min-w-0 flex-1 space-y-2">
                  <p className="text-sm text-ink">
                    <span className="text-ink-3">Name as typed: </span>
                    {data.fullName}
                  </p>
                  {data.phone && (
                    <p className="text-sm text-ink">
                      <span className="text-ink-3">Phone: </span>
                      {data.phone}
                    </p>
                  )}
                  {data.documents.length === 0 ? (
                    <p className="text-sm text-ink-3">No ID document on file.</p>
                  ) : (
                    <ul className="space-y-1">
                      {data.documents.map((doc) => (
                        <li key={doc.id} className="flex items-center gap-2 text-sm">
                          <span className="text-ink">
                            {DOC_TYPE_LABEL[doc.docType]} — {SIDE_LABEL[doc.side]}
                          </span>
                          <button
                            type="button"
                            className="link text-xs"
                            disabled={viewDocument.isPending}
                            onClick={() =>
                              viewDocument.mutate({ tutorId, documentId: doc.id })
                            }
                          >
                            View
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Said out loud, because it is: every view is recorded
                      against the admin who made it. */}
                  <p className="text-xs text-ink-3">Opening a document is logged.</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">Teaching</h3>
              {data.bio ? (
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-2">
                  {data.bio}
                </p>
              ) : (
                <p className="text-sm text-ink-3">No bio.</p>
              )}
              <p className="mt-2 text-xs text-ink-3">
                {data.subjectIds.length} subject{data.subjectIds.length === 1 ? '' : 's'} ·{' '}
                {data.levelIds.length} stage{data.levelIds.length === 1 ? '' : 's'}
                {data.yearsExperience !== undefined && ` · ${data.yearsExperience} years`}
              </p>
              {data.introVideoUrl && (
                <a
                  className="link mt-2 inline-block text-xs"
                  href={data.introVideoUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Watch the intro video
                </a>
              )}
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">Getting paid</h3>
              {data.payoutAccount ? (
                <div className="space-y-1 text-sm">
                  <p className="text-ink">
                    {data.payoutAccount.method.nameEn}
                    {data.payoutAccount.bankName && ` · ${data.payoutAccount.bankName}`}
                  </p>
                  <p className="font-mono text-ink-2" dir="ltr">
                    {reveal.data?.accountRef ?? data.payoutAccount.accountRefMasked}
                  </p>
                  {reveal.data?.accountNumber && (
                    <p className="font-mono text-xs text-ink-2" dir="ltr">
                      Account number: {reveal.data.accountNumber}
                    </p>
                  )}
                  {data.payoutAccount.holderName && (
                    <p className="text-ink">
                      <span className="text-ink-3">Holder: </span>
                      {data.payoutAccount.holderName}{' '}
                      {/* The hint, not a verdict: Arabic and English
                          transliterations of one name differ, which is why
                          the API refuses nothing on this. */}
                      <span
                        className={
                          data.holderNameMatchesId ? 'text-xs text-good' : 'text-xs text-ink-3'
                        }
                      >
                        {data.holderNameMatchesId
                          ? '· looks like the name above'
                          : '· check this against the ID'}
                      </span>
                    </p>
                  )}
                  {!reveal.data && (
                    <button
                      type="button"
                      className="link text-xs"
                      disabled={reveal.isPending}
                      onClick={() => reveal.mutate({ tutorId })}
                    >
                      Reveal the full number (logged)
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-ink-3">No payout account yet.</p>
              )}
            </section>

            {data.missing.length > 0 && (
              <section className="rounded-lg border border-rule bg-surface-2 p-3">
                <h3 className="text-sm font-semibold text-ink">Still missing</h3>
                <p className="mt-1 text-sm text-ink-2">{data.missing.join(', ')}</p>
              </section>
            )}

            {data.reviewNote && (
              <section>
                <h3 className="mb-1 text-sm font-semibold text-ink">Last review note</h3>
                <p className="text-sm text-ink-2">{data.reviewNote}</p>
                {data.reviewedAt && (
                  <p className="mt-1 text-xs text-ink-3">{formatDateTime(data.reviewedAt)}</p>
                )}
              </section>
            )}
          </div>
        )}
      </div>

      {data && (
        <footer className="flex flex-wrap gap-2 border-t border-rule px-5 py-3">
          {awaitingReview ? (
            <>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setDialog('approve')}
              >
                Approve
              </button>
              <button type="button" className="btn" onClick={() => setDialog('changes')}>
                Request changes
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setDialog('reject')}
              >
                Reject
              </button>
            </>
          ) : data.applicationStatus === 'approved' ? (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setDialog('suspend')}
            >
              Suspend
            </button>
          ) : (
            <p className="text-xs text-ink-3">
              Nothing to decide — this application is not awaiting review.
            </p>
          )}
        </footer>
      )}

      {/* Approve needs the commission, because an approved tutor without one
          could teach lessons nobody knows how to split — and the database
          refuses the transition anyway. */}
      <Modal
        open={dialog === 'approve'}
        onClose={closeDialog}
        title="Approve this tutor"
        description="Set the platform's share of each lesson. This becomes their first commission rate."
        footer={
          <>
            <button type="button" className="btn" onClick={closeDialog}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={pending || commission === ''}
              onClick={() =>
                void run(() =>
                  approve.mutateAsync({
                    tutorId,
                    commissionPercent: Number(commission),
                    note: note.trim() || undefined,
                  }),
                )
              }
            >
              {pending && <Spinner />}
              Approve
            </button>
          </>
        }
      >
        <label className="label" htmlFor="commissionPercent">
          Commission %
        </label>
        <input
          id="commissionPercent"
          type="number"
          min={0}
          max={100}
          step={0.01}
          className="input"
          value={commission}
          onChange={(e) => setCommission(e.target.value)}
        />
        <label className="label mt-3" htmlFor="approveNote">
          Note (optional)
        </label>
        <textarea
          id="approveNote"
          rows={2}
          className="input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {actionError && (
          <p role="alert" className="mt-2 text-sm text-danger">
            {actionError}
          </p>
        )}
      </Modal>

      <Modal
        open={dialog === 'changes'}
        onClose={closeDialog}
        title="Request changes"
        description="The note reaches the tutor, and the fields you tick are highlighted on their form."
        footer={
          <>
            <button type="button" className="btn" onClick={closeDialog}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={pending || !note.trim()}
              onClick={() =>
                void run(() =>
                  requestChanges.mutateAsync({
                    tutorId,
                    note: note.trim(),
                    fields: fields.length > 0 ? fields : undefined,
                  }),
                )
              }
            >
              {pending && <Spinner />}
              Send
            </button>
          </>
        }
      >
        <label className="label" htmlFor="changesNote">
          What needs changing
        </label>
        <textarea
          id="changesNote"
          rows={3}
          className="input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <fieldset className="mt-3">
          <legend className="label">Highlight</legend>
          <div className="flex flex-wrap gap-2">
            {CHANGEABLE.map((f) => (
              <label key={f.value} className="flex items-center gap-1.5 text-sm text-ink-2">
                <input
                  type="checkbox"
                  className="size-4"
                  checked={fields.includes(f.value)}
                  onChange={() =>
                    setFields((cur) =>
                      cur.includes(f.value)
                        ? cur.filter((v) => v !== f.value)
                        : [...cur, f.value],
                    )
                  }
                />
                {f.label}
              </label>
            ))}
          </div>
        </fieldset>
        {actionError && (
          <p role="alert" className="mt-2 text-sm text-danger">
            {actionError}
          </p>
        )}
      </Modal>

      <Modal
        open={dialog === 'reject' || dialog === 'suspend'}
        onClose={closeDialog}
        title={dialog === 'suspend' ? 'Suspend this tutor' : 'Reject this application'}
        description={
          dialog === 'suspend'
            ? 'They come off the roster and can be given nothing new. Lessons already booked still go ahead.'
            : 'Final for this account. The applicant can contact support.'
        }
        footer={
          <>
            <button type="button" className="btn" onClick={closeDialog}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={pending || !note.trim()}
              onClick={() =>
                void run(() =>
                  dialog === 'suspend'
                    ? suspend.mutateAsync({ tutorId, note: note.trim() })
                    : reject.mutateAsync({ tutorId, note: note.trim() }),
                )
              }
            >
              {pending && <Spinner />}
              {dialog === 'suspend' ? 'Suspend' : 'Reject'}
            </button>
          </>
        }
      >
        <label className="label" htmlFor="rejectNote">
          Reason (the tutor reads this)
        </label>
        <textarea
          id="rejectNote"
          rows={3}
          className="input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {actionError && (
          <p role="alert" className="mt-2 text-sm text-danger">
            {actionError}
          </p>
        )}
      </Modal>
    </aside>
  );
}
